import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  Scale,
  FileText,
  CheckCircle,
  ShieldCheck,
  BookOpenCheck,
  ClipboardCheck,
  QrCode,
  Mail,
  Phone,
  ArrowRight,
  BookMarked,
  Award,
} from "lucide-react";
import ContactFormDialog from "../components/ContactFormDialog";

const OfferCard = ({ icon: Icon, title, description, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-brand-line rounded-sm p-8 hover:shadow-md hover:border-brand-blue/30 transition-[box-shadow,border-color] duration-200"
  >
    <div className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-brand-blue/5 text-brand-blue mb-5">
      <Icon size={22} strokeWidth={1.75} />
    </div>
    <h3 className="text-xl text-brand-blue mb-2">{title}</h3>
    <p className="text-brand-muted text-base leading-relaxed">{description}</p>
  </div>
);

const ReasonItem = ({ icon: Icon, title, description, testid }) => (
  <div data-testid={testid} className="flex items-start gap-4">
    <div className="shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-sm bg-white border border-brand-line text-brand-gold">
      <Icon size={20} strokeWidth={1.75} />
    </div>
    <div>
      <h4 className="text-lg text-brand-blue mb-1">{title}</h4>
      <p className="text-brand-muted text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

export default function Landing() {
  const [contactOpen, setContactOpen] = useState(false);
  return (
    <div data-testid="landing-page" className="min-h-screen bg-white">
      {/* Public Topbar */}
      <header
        data-testid="landing-topbar"
        className="bg-white border-b border-brand-line h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8 sticky top-0 z-40"
      >
        <div className="flex items-center gap-2.5">
          <img
            src={`${process.env.PUBLIC_URL}/logo-academy-full.png`}
            alt="Buro Academy"
            className="h-7 w-auto"
          />
        </div>
        <nav aria-label="Navigazione principale" className="flex items-center gap-5">
          <Link
            to="/verifica-attestato"
            data-testid="topbar-verify-link"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-brand-muted hover:text-brand-blue transition-colors"
          >
            <ShieldCheck size={14} /> Verifica attestato
          </Link>
          <Link
            to="/corsi"
            data-testid="topbar-catalog-link"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-brand-muted hover:text-brand-blue transition-colors"
          >
            <BookMarked size={14} /> Catalogo Corsi
          </Link>
          <Link
            to="/login"
            data-testid="topbar-login-link"
            className="btn-primary text-sm inline-flex items-center gap-2"
          >
            Accedi <ArrowRight size={16} />
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative brand-grid-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-blue bg-brand-blue/5 border border-brand-blue/10 rounded-sm px-3 py-1.5 mb-6"
            >
              <ShieldCheck size={14} /> Formazione professionale certificata
            </div>
            <h1
              data-testid="hero-title"
              className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-brand-ink mb-6"
            >
              Formazione specialistica per{" "}
              <span className="text-brand-blue">gare d’appalto</span>, offerte tecniche
              e sistemi <span className="italic">ISO</span>.
            </h1>
            <p
              data-testid="hero-subtitle"
              className="text-lg text-brand-muted leading-relaxed max-w-2xl mb-8"
            >
              Buro Academy è la piattaforma di Burocratica Servizi per
              professionisti, imprese e pubbliche amministrazioni che vogliono
              acquisire competenze concrete su appalti pubblici, redazione delle
              offerte tecniche e sistemi di gestione ISO.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/login"
                data-testid="hero-cta-login"
                className="btn-primary inline-flex items-center gap-2"
              >
                Accedi alla piattaforma <ArrowRight size={18} />
              </Link>
              <a
                href="#cosa-offriamo"
                data-testid="hero-cta-scroll"
                className="text-brand-blue font-semibold text-sm hover:underline underline-offset-4"
              >
                Scopri i percorsi formativi →
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-3 -left-3 h-20 w-20 border-l-2 border-t-2 border-brand-gold" />
              <div className="absolute -bottom-3 -right-3 h-20 w-20 border-r-2 border-b-2 border-brand-blue" />
              <img
                data-testid="hero-image"
                src="https://images.pexels.com/photos/8761327/pexels-photo-8761327.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Formazione professionale Buro Academy"
                className="w-full h-[420px] object-cover rounded-sm border border-brand-line shadow-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Cosa offriamo */}
      <section
        id="cosa-offriamo"
        data-testid="cosa-offriamo-section"
        className="py-20 md:py-28 bg-brand-bg border-y border-brand-line"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold mb-3">
              — Programmi formativi
            </div>
            <h2 className="text-3xl sm:text-4xl text-brand-ink mb-4">
              Cosa offriamo
            </h2>
            <p className="text-brand-muted text-base leading-relaxed">
              Quattro aree tematiche integrate per formare professionisti capaci
              di affrontare l’intero ciclo dell’appalto pubblico, i requisiti dei
              principali standard internazionali e la qualificazione SOA.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <OfferCard
              testid="offer-card-gare"
              icon={Scale}
              title="Gare d’Appalto"
              description="Codice dei Contratti Pubblici, procedure di gara, criteri di aggiudicazione, MEPA e piattaforme telematiche."
            />
            <OfferCard
              testid="offer-card-offerte"
              icon={FileText}
              title="Offerte Tecniche"
              description="Strategia, struttura e redazione di offerte tecniche vincenti, con focus su qualità documentale e punteggi."
            />
            <OfferCard
              testid="offer-card-iso"
              icon={CheckCircle}
              title="Sistemi ISO"
              description="ISO 9001, 14001, 45001, 37001: implementazione, audit interni e conformità dei sistemi di gestione."
            />
            <OfferCard
              testid="offer-card-soa"
              icon={Award}
              title="Attestazione SOA"
              description="Requisiti, categorie e classifiche per la qualificazione all’esecuzione di lavori pubblici, e mantenimento dell’attestazione."
            />
          </div>
        </div>
      </section>

      {/* Catalog CTA banner */}
      <section
        data-testid="catalog-cta-section"
        className="py-14 bg-white border-b border-brand-line"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-[1fr_auto] items-center gap-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
              — Catalogo formativo
            </div>
            <h2 className="text-2xl sm:text-3xl text-brand-ink mb-2">
              Sfoglia tutti i corsi con dettagli, durate e prezzi per addetto
            </h2>
            <p className="text-brand-muted max-w-2xl">
              Ogni corso include un modulo di iscrizione scaricabile con sconti
              progressivi per gruppi di lavoro.
            </p>
          </div>
          <Link
            to="/corsi"
            data-testid="landing-catalog-cta"
            className="btn-primary inline-flex items-center gap-2 whitespace-nowrap text-base px-6 py-3"
          >
            <BookMarked size={18} /> Vedi i corsi disponibili
          </Link>
        </div>
      </section>

      {/* Perché sceglierci */}
      <section
        data-testid="perche-section"
        className="py-20 md:py-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold mb-3">
              — Il metodo Burocratica
            </div>
            <h2 className="text-3xl sm:text-4xl text-brand-ink mb-5">
              Perché sceglierci
            </h2>
            <p className="text-brand-muted text-base leading-relaxed">
              Un approccio istituzionale alla formazione: contenuti verificati,
              docenti operativi sul campo e strumenti pensati per l’applicazione
              reale in azienda o in ente pubblico.
            </p>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-8">
            <ReasonItem
              testid="reason-docenti"
              icon={GraduationCap}
              title="Docenti esperti"
              description="Consulenti e professionisti attivi in gare d’appalto e certificazioni ISO."
            />
            <ReasonItem
              testid="reason-materiali"
              icon={BookOpenCheck}
              title="Materiali scaricabili"
              description="Slide, template, checklist e normative sempre aggiornate e disponibili."
            />
            <ReasonItem
              testid="reason-test"
              icon={ClipboardCheck}
              title="Test di verifica"
              description="Quiz di autovalutazione al termine di ogni modulo per consolidare le competenze."
            />
            <ReasonItem
              testid="reason-certificato"
              icon={QrCode}
              title="Certificato con QR di autenticità"
              description="Attestato finale verificabile tramite codice QR di autenticità univoco."
            />
          </div>
        </div>
      </section>

      {/* Contatti */}
      <section
        data-testid="contatti-section"
        className="py-20 md:py-24 bg-brand-blue text-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <div className="text-xs font-semibold uppercase tracking-widest text-brand-bg mb-3">
              — Contatti
            </div>
            <h2 className="text-3xl sm:text-4xl mb-4 text-white">
              Parla con Burocratica Servizi
            </h2>
            <p className="text-white/80 text-base leading-relaxed max-w-lg mb-6">
              Per informazioni sui percorsi, convenzioni per enti pubblici o
              formazione aziendale personalizzata, contattaci direttamente.
            </p>
            <button
              onClick={() => setContactOpen(true)}
              data-testid="landing-contact-btn"
              className="inline-flex items-center gap-2 rounded-sm bg-white text-brand-blue font-semibold px-5 py-2.5 hover:bg-white/90 transition-colors"
            >
              <Mail size={16} /> Contatta l'amministrazione
            </button>
          </div>
          <div className="lg:col-span-6 grid sm:grid-cols-2 gap-4">
            <a
              href="mailto:info@burocraticaservizi.it"
              data-testid="contact-email"
              className="group bg-white/5 border border-white/15 rounded-sm p-6 hover:bg-white/10 transition-colors"
            >
              <Mail size={20} className="text-brand-gold mb-3" />
              <div className="text-xs uppercase tracking-widest text-white/60 mb-1">
                Email
              </div>
              <div className="text-base font-semibold">
                info@burocraticaservizi.it
              </div>
            </a>
            <a
              href="tel:+39011393770"
              data-testid="contact-phone"
              className="group bg-white/5 border border-white/15 rounded-sm p-6 hover:bg-white/10 transition-colors"
            >
              <Phone size={20} className="text-brand-gold mb-3" />
              <div className="text-xs uppercase tracking-widest text-white/60 mb-1">
                Telefono
              </div>
              <div className="text-base font-semibold">+39 011 393 770</div>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        data-testid="landing-footer"
        className="bg-white border-t border-brand-line py-10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <img
                src={`${process.env.PUBLIC_URL}/logo-academy-full.png`}
                alt="Buro Academy"
                className="h-7 w-auto"
              />
            </div>
            <div className="text-sm text-brand-muted">
              © {new Date().getFullYear()} Burocratica Servizi — Tutti i diritti riservati
            </div>
          </div>
          <nav
            data-testid="footer-legal-links"
            aria-label="Link legali"
            className="flex flex-wrap justify-center sm:justify-start gap-x-6 gap-y-2 text-sm text-brand-muted"
          >
            <a href="/privacy" className="hover:text-brand-blue transition-colors">
              Privacy
            </a>
          </nav>
        </div>
      </footer>

      <ContactFormDialog open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
}
